// Trust-page data — V10c
// Founder + leadership/educator team profiles, each carrying Person schema.
// Used by /founder and /team to anchor Knowledge-Graph "people behind the brand".

export const FOUNDER = {
    name: "Anand Rajput",
    role: "Founder & Managing Director, Anand Tutorials",
    image: "https://anandtutorials.com/team/founder.jpg",
    foundedYear: 2013,
    location: "Jabalpur, Madhya Pradesh, India",
    education: [
        "B.Tech in Mechanical Engineering",
        "Specialist in CBSE / ICSE / MP-Board curriculum design",
    ],
    expertise: [
        "1:1 Home Tuition Operations",
        "Tutor Verification & Onboarding",
        "Curriculum Mapping (CBSE / ICSE / IB / IGCSE)",
        "Parent Engagement & Outcomes",
    ],
    quote: "Every child has a different ceiling — and every ceiling has a different ladder. Our entire model exists to find that ladder.",
    bio: [
        "Anand founded Anand Tutorials in 2013 in his home city of Jabalpur, Madhya Pradesh — driven by a single observation: the children most in need of focused mentoring were the ones most likely to be lost inside large coaching batches. The structural fix was obvious. The discipline of executing it consistently, every day, for 12+ years, is what built the institution.",
        "From a single CBSE Class 10 student in 2013, Anand Tutorials has grown to 50,000+ taught students across India and 27 international regions — every single one of them in a strict 1 tutor : 1 student setup. That structural rule has never been compromised, regardless of demand.",
        "Anand personally interviews senior tutor candidates, runs the bi-annual tutor-panel review, and reads a sample of the Sunday parent reports every week. The founder-quality bar — verify the tutor, plan the term, report to the parent, refund if it fails — is set and protected by him.",
        "Beyond operations, Anand is an active voice in Indian 1:1 education policy and parent advocacy. He has spoken at education forums on background-verification standards for private tutors, on the structural failure of 'small group' coaching for K-12, and on responsible online tutoring for NRI families.",
    ],
    achievements: [
        { year: "2013", text: "Founded Anand Tutorials with a strict 1:1 home-tuition model in Jabalpur." },
        { year: "2016", text: "Expanded operations to Bhopal and opened second branch in Jabalpur." },
        { year: "2019", text: "Launched 1:1 live online tutoring for NRI families and pan-India students." },
        { year: "2021", text: "Codified 4-step tutor verification (credential + subject test + demo + police check)." },
        { year: "2023", text: "Crossed 30,000 cumulative students taught; opened Nagpur operations." },
        { year: "2025", text: "Active student count crosses 5,000 globally; tutor panel grows past 2,500 verified specialists." },
        { year: "2026", text: "50,000+ students taught; 27 international regions served; published open Parent & Refund policies." },
    ],
    sameAs: [
        "https://www.facebook.com/anandtutorialsofficial",
        "https://www.instagram.com/anandtutorialsofficial",
    ],
};

export const LEADERSHIP_TEAM = [
    {
        slug: "nitesh-rajput",
        name: "Nitesh Rajput",
        role: "Chief Operations & Technology Officer (COO)",
        bio: "Nitesh leads overall business operations and technology management at Anand Tutorials. He oversees day-to-day operations, coordinates with the development team for continuous improvement of the platform and mobile applications, and ensures every internal process runs efficiently and systematically. He plays a key role in operational planning, process optimisation, team coordination, and implementing technology-driven solutions for a seamless experience across students, parents, tutors, and staff.",
        expertise: [
            "Business Operations Management",
            "App & Platform Management",
            "Process Design & Workflow Optimisation",
            "Team Coordination & Strategy",
            "Technology Implementation",
            "Cross-Department Performance Monitoring",
        ],
    },
    {
        slug: "aditya-rajput",
        name: "Aditya Rajput",
        role: "Operations & Student Success Director",
        bio: "Aditya oversees tutor allocation and parent relationship management at Anand Tutorials. He ensures every student is matched with the most suitable tutor based on academic needs and learning goals. He monitors parent feedback, coordinates issue resolution, and works continuously to maintain high service quality and customer satisfaction.",
        expertise: [
            "Tutor Assignment & Matching",
            "Parent Feedback Management",
            "Student-Tutor Coordination",
            "Service Quality Monitoring",
            "Issue Resolution & Follow-up",
            "Ongoing Student Success Support",
        ],
    },
    {
        slug: "nilesh-rajput",
        name: "Nilesh Rajput",
        role: "Finance Director",
        bio: "Nilesh oversees the Finance & Accounts department at Anand Tutorials, ensuring smooth and transparent fee management for parents and students. He supervises fee collection, payment records, billing processes, and financial coordination while maintaining accuracy and timely support — helping ensure a seamless and trustworthy financial experience for every family.",
        expertise: [
            "Fee Management & Collection",
            "Parent Billing & Payment Support",
            "Accounts & Financial Records",
            "Payment Verification & Reconciliation",
            "Financial Process Coordination",
        ],
    },
];

// Person schema generator — used in /founder and /team pages.
// V61 Phase 6 (AEO): now supports knowsAbout, alumniOf, birthPlace,
// jobTitle nesting, foundingDate, awards + honorificPrefix for richer
// Knowledge Graph / LLM answer engine extraction.
export function personSchema({
    name, role, url, image, sameAs = [], bio = "",
    knowsAbout = [], alumniOf = null, birthPlace = null,
    honorificPrefix = null, founderOf = false, awards = [],
    knowsLanguage = [],
}) {
    const base = {
        "@context": "https://schema.org",
        "@type": "Person",
        "name": name,
        "jobTitle": role,
        "worksFor": {
            "@type": "EducationalOrganization",
            "@id": "https://anandtutorials.com/#organization",
            "name": "Anand Tutorials",
            "url": "https://anandtutorials.com",
        },
    };
    if (url) base["@id"] = `${url}#person`;
    if (url) base["url"] = url;
    if (image) base["image"] = image;
    if (sameAs && sameAs.length > 0) base["sameAs"] = sameAs;
    if (bio) base["description"] = bio;
    if (knowsAbout.length > 0) base["knowsAbout"] = knowsAbout;
    if (knowsLanguage.length > 0) base["knowsLanguage"] = knowsLanguage;
    if (alumniOf) {
        base["alumniOf"] = alumniOf.map((a) => (typeof a === "string"
            ? { "@type": "CollegeOrUniversity", "name": a }
            : a));
    }
    if (birthPlace) base["birthPlace"] = { "@type": "Place", "name": birthPlace };
    if (honorificPrefix) base["honorificPrefix"] = honorificPrefix;
    if (founderOf) {
        base["founder"] = true;
        // If founderOf is an object (org schema stub), attach as founderOf link
        if (typeof founderOf === "object") {
            base["memberOf"] = { "@id": founderOf["@id"] || "https://anandtutorials.com/#organization" };
        } else {
            base["memberOf"] = { "@id": "https://anandtutorials.com/#organization" };
        }
    }
    if (awards.length > 0) base["award"] = awards;
    return base;
}
